/* eslint-disable @typescript-eslint/no-use-before-define */
import omit from 'lodash/omit';
import { defineModule } from 'direct-vuex';
import { moduleActionContext, moduleGetterContext } from '@/plugins/vuex';
import { TargetOptions, adapters } from '@aison/migrator';

import { aison, ConnectRequest, ReadRequest } from '@/api';
import { SimpleTransformField } from '@/types';

type TargetType = 'aison'; // TODO: add aison, etc.

export interface TargetState {
  targetType: TargetType;
  type?: adapters.OutputAdapterType;
  adapter: adapters.AdapterOptions;
  // Target adapter
  adapterConnected: boolean;
  adapterLoading: boolean;
  adapterError?: object;
  // Aison specific
  appsLoading: boolean;
  apps: object[];
  appId?: string;
  datasetsLoading: boolean;
  datasets: object[];
  dataset?: string;
  fields: object[];
  linkedAppLoading: boolean;
  linkedAppFields: object[];
}

export interface QueryOptions {
  run: boolean;
  query: string;
}

const initialState = (): TargetState => ({
  targetType: 'aison',
  type: 'aison-record',
  adapter: {},
  // Target adapter
  adapterConnected: false,
  adapterLoading: false,
  adapterError: undefined,
  // Aison specific
  appsLoading: false,
  apps: [],
  appId: undefined,
  datasetsLoading: false,
  datasets: [],
  dataset: undefined,
  fields: [],
  linkedAppLoading: false,
  linkedAppFields: [],
});

const module = defineModule({
  namespaced: true,
  state: initialState,
  getters: {
    queries: (...args) => targetGetterContext(args).state.queries,
    currentQuery(...args) {
      const { state } = targetGetterContext(args);
      return state.queries[state.currentQuery];
    },
    currentQueryIndex: (...args) =>
      targetGetterContext(args).state.currentQuery,
    connectRequest(...args): ConnectRequest {
      const { state } = targetGetterContext(args);
      return {
        type: state.type,
        adapter: state.adapter,
      };
    },
    appsRequest(...args): ReadRequest {
      const { getters } = targetGetterContext(args);
      return {
        ...getters.connectRequest,
        read: {},
      };
    },
    appRequest(...args): ReadRequest {
      const { state, getters } = targetGetterContext(args);
      return {
        ...getters.connectRequest,
        read: { appId: state.appId, datasets: true },
      };
    },
    linkedAppRequest(...args): ReadRequest {
      const { getters } = targetGetterContext(args);
      return {
        ...getters.connectRequest,
        read: { appId: getters.linkedAppId, datasets: false },
      };
    },
    config(...args): TargetOptions {
      const { state } = targetGetterContext(args);
      return {
        type: state.type,
        adapter: {
          ...omit(state.adapter, ['authType']),
          batch: true,
          appId: state.appId,
          dataset: state.dataset,
        } as adapters.AisonRecordAdapterOptions,
      };
    },
    fields(...args): SimpleTransformField[] {
      const { state } = targetGetterContext(args);
      return state.fields.map(field => ({
        value: field.store_as,
        text: field.store_as,
        subtitle: `${field.legend} | ${field.data_type}`,
      }));
    },
    linkedFields(...args): SimpleTransformField[] {
      const { state } = targetGetterContext(args);

      return state.fields
        .filter(field => field.data_type === 'Link')
        .map(field => ({
          value: field.store_as,
          text: `${field.store_as} (${field.app}@${field.view})`,
          subtitle: `${field.legend} | ${field.data_type}`,
        }));
    },
    externalIdFieldAlternatives(...args): SimpleTransformField[] {
      const { state } = targetGetterContext(args);

      return state.fields
        .filter(field => field.data_type !== 'Link')
        .map(field => ({
          value: field.store_as,
          text: field.store_as,
          subtitle: `${field.legend} | ${field.data_type}`,
        }));
    },
    linkedAppId(...args): string | undefined {
      const { state } = targetGetterContext(args);
      const linkField = state.adapter.linkField;

      if (!linkField) {
        return;
      }

      const field = state.fields.find(field => field.store_as === linkField);
      if (!field) {
        return;
      }

      return `${field.app}@${field.view}`;
    },
    linkedAppExternalIdFieldAlternatives(...args): SimpleTransformField[] {
      const { state } = targetGetterContext(args);

      if (!state.linkedAppFields?.length) {
        return [];
      }
      return state.linkedAppFields
        .filter(field => field.data_type !== 'Link')
        .map(field => ({
          value: field.store_as,
          text: field.store_as,
          subtitle: `${field.legend} | ${field.data_type}`,
        }));
    },
  },
  mutations: {
    reset(state, options = {}) {
      Object.assign(state, initialState(), options);
    },
    setTargetType(state, value: TargetType) {
      state.targetType = value;
    },
    setType(state, value: adapters.OutputAdapterType) {
      state.type = value;
    },
    setAppsLoading(state) {
      state.apps = [];
      state.appsLoading = true;
    },
    setApps(state, apps: object[]) {
      state.apps = apps;
      state.appsLoading = false;
      state.adapterError = undefined;
    },
    setDatasetsLoading(state) {
      state.datasets = [];
      state.fields = [];
      state.datasetsLoading = true;
    },
    setDatasets(state, datasets: object[]) {
      state.datasets = datasets;
      state.datasetsLoading = false;
      state.adapterError = undefined;
    },
    setAppData(state, value: any) {
      state.fields = value?.fields || [];
    },
    setAppId(state, value: string) {
      state.appId = value;
    },
    setDataset(state, value: string) {
      state.dataset = value;
    },
    setAdapter(state, adapter: object) {
      // TODO: reset connected here?
      state.adapter = adapter;
    },
    setAdapterLoading(state) {
      state.adapterLoading = true;
      state.adapterConnected = false;
      state.adapterError = undefined;
    },
    setAdapterError(state, error: object) {
      state.adapterLoading = false;
      state.appsLoading = false;
      state.datasetsLoading = false;
      state.adapterError = error;
    },
    setAdapterConnected(state, connected: boolean) {
      state.adapterConnected = connected;
      state.adapterLoading = false;
      if (connected) {
        state.adapterError = undefined;
      }
    },
    setLinkedAppLoading(state) {
      state.linkedAppLoading = true;
      state.linkedAppFields = [];
    },
    setLinkedAppData(state, value: any) {
      state.linkedAppLoading = false;
      state.linkedAppFields = value?.fields || [];
    },
  },
  actions: {
    reset(context, payload: TargetOptions) {
      const { commit } = targetActionContext(context);
      // TODO: Parse values into options properlyyy
      commit.reset(payload);
    },
    updateAdapter(context, payload: { adapter: object }) {
      const { commit } = targetActionContext(context);
      commit.setAdapterConnected(false);
      commit.setAdapter(payload.adapter);
    },
    async connectAdapter(context) {
      const { commit, getters } = targetActionContext(context);
      commit.setAdapterLoading(null);
      try {
        // await new Promise(resolve => setTimeout(resolve, 5000));
        await aison.connect(getters.connectRequest);
        commit.setAdapterConnected(true);
      } catch (error) {
        if (error?.message) {
          commit.setAdapterError(error.message);
        } else {
          commit.setAdapterError(error);
        }
        commit.setAdapterConnected(false);
      }
    },
    async loadApps(context) {
      const { commit, getters } = targetActionContext(context);
      commit.setAppsLoading(true);
      try {
        // await new Promise(resolve => setTimeout(resolve, 5000));
        const { apps } = await aison.read(getters.appsRequest);
        commit.setApps(apps);
      } catch (error) {
        if (error?.message) {
          commit.setAdapterError(error.message);
        } else {
          commit.setAdapterError(error);
        }
      }
    },
    async loadApp(context) {
      const { commit, getters } = targetActionContext(context);
      commit.setDatasetsLoading(true);
      try {
        // await new Promise(resolve => setTimeout(resolve, 5000));
        const { datasets, app } = await aison.read(getters.appRequest);
        commit.setDatasets(datasets);
        commit.setAppData(app);
      } catch (error) {
        // eslint-disable-next-line
        console.warn('Failed loading app', error);
        if (error?.message) {
          commit.setAdapterError(error.message);
        } else {
          commit.setAdapterError(error);
        }
      }
    },
    async loadLinkedApp(context) {
      const { commit, getters } = targetActionContext(context);
      commit.setLinkedAppLoading(true);
      try {
        const { app } = await aison.read(getters.linkedAppRequest);
        commit.setLinkedAppData(app);
      } catch (error) {
        // eslint-disable-next-line
        console.warn('Failed to load linked app', error);
      }
    },
  },
});

const targetGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, module);
const targetActionContext = (context: any) =>
  moduleActionContext(context, module);

export default module;
